import React, { useState } from "react";
import { X, Plus } from "lucide-react";
import { Dropdown, Input, Button } from './ui';

const CustomFieldFilter = ({
  onFiltersChange,
  currentFilters = [],
  fieldOptions = [
    // Default field options with user-friendly labels (for clients)
    { key: "brand", label: "Brand", type: "array" },
    { key: "trimbleFormats", label: "Trimble Formats", type: "array" },
    { key: "servicesProvided", label: "Services Provided", type: "array" },
    { key: "leadSource", label: "Lead Source", type: "string" },
    { key: "dealerName", label: "Dealer Name", type: "string" },
    { key: "contractorName", label: "Contractor Name", type: "string" },
    { key: "threeDUtilities", label: "3D Utilities", type: "string" },
    { key: "levelOfDetail", label: "Level of Detail", type: "string" },
    { key: "platformOther", label: "Platform (Other)", type: "string" },
    { key: "pricingStatus", label: "Pricing Status", type: "string" },
    { key: "terms", label: "Terms", type: "number" },
    { key: "acquisitionSource", label: "Acquisition Source", type: "string" },
  ]
}) => {
  const [filters, setFilters] = useState(currentFilters);

  const filterConditions = [
    { value: "equals", label: "Equals", needsValue: true },
    { value: "contains", label: "Contains", needsValue: true },
    { value: "starts_with", label: "Starts with", needsValue: true },
    { value: "ends_with", label: "Ends with", needsValue: true },
    { value: "is_empty", label: "Is empty", needsValue: false },
    { value: "is_not_empty", label: "Is not empty", needsValue: false },
    { value: "not_equals", label: "Does not equal", needsValue: true },
    { value: "not_contains", label: "Does not contain", needsValue: true },
  ];

  const addFilter = () => {
    const newFilter = {
      id: Date.now(),
      field: fieldOptions[0].key,
      condition: "equals",
      value: "",
    };
    const newFilters = [...filters, newFilter];
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const removeFilter = (filterId) => {
    const newFilters = filters.filter(f => f.id !== filterId);
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const updateFilter = (filterId, field, value) => {
    const newFilters = filters.map(f =>
      f.id === filterId ? { ...f, [field]: value } : f
    );
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {/* Active Filters */}
      {filters.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {filters.map((filter) => {
            const selectedCondition = filterConditions.find(c => c.value === filter.condition);
            const needsValue = selectedCondition?.needsValue ?? true;

            return (
              <div
                key={filter.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: needsValue ? 'minmax(150px, 1fr) minmax(150px, 1fr) minmax(150px, 1fr) auto' : 'minmax(150px, 1fr) minmax(150px, 1fr) auto',
                  gap: 'var(--space-2)',
                  alignItems: 'end',
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                {/* Field Dropdown */}
                <div style={{ minWidth: 0 }}>
                  <Dropdown
                    value={filter.field}
                    options={fieldOptions.map(f => ({ value: f.key, label: f.label }))}
                    onChange={(value) => updateFilter(filter.id, 'field', value)}
                    placeholder="Select field..."
                    size="sm"
                  />
                </div>

                {/* Condition Dropdown */}
                <div style={{ minWidth: 0 }}>
                  <Dropdown
                    value={filter.condition}
                    options={filterConditions.map(c => ({ value: c.value, label: c.label }))}
                    onChange={(value) => updateFilter(filter.id, 'condition', value)}
                    placeholder="Select condition..."
                    size="sm"
                  />
                </div>

                {/* Value Input (only if needed) */}
                {needsValue && (
                  <div style={{ minWidth: 0 }}>
                    <Input
                      type="text"
                      value={filter.value}
                      onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                      placeholder="Enter value..."
                      size="sm"
                    />
                  </div>
                )}

                {/* Remove Button */}
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFilter(filter.id)}
                    icon={<X size={16} />}
                    style={{ padding: '8px', minWidth: '36px', height: '36px' }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            padding: 'var(--space-4)',
            textAlign: 'center',
            color: 'var(--gray-400)',
            fontSize: 'var(--text-sm)',
          }}
        >
          No custom filters active
        </div>
      )}

      {/* Add Filter Button */}
      <Button
        variant="secondary"
        size="sm"
        onClick={addFilter}
        icon={<Plus size={16} />}
        style={{ alignSelf: 'flex-start' }}
      >
        Add Custom Filter
      </Button>
    </div>
  );
};

export default CustomFieldFilter;
